import { useId, useState, type CSSProperties } from 'react';
import { Outlet, useNavigate } from 'react-router';

import { signOut } from '@shared/auth/client';
import { copy } from '@shared/copy';
import { Footer } from '@shared/chrome/Footer';
import { SideNav } from '@shared/chrome/SideNav';
import { Topbar } from '@shared/chrome/Topbar';
import { ConfirmDialog } from '@shared/ui/dialog';
import { useDocumentTitle } from '@shared/chrome/useDocumentTitle';

import { useNavStore, useNavTierSync } from './nav-state';

/**
 * One shared grid, mounted once by a layout `RouteObject` in
 * `src/router/index.tsx`. Every route below the auth shell renders inside it.
 *
 * One `<header role="banner">`, one `<main id="main">`, one `<aside>` navigation
 * landmark, one `<footer>`. The skip link is the first focusable element in the
 * document.
 *
 * The drawer and its scrim are **siblings of the chrome**, never children: the
 * top bar takes a `backdrop-filter` once it solidifies, and a `backdrop-filter`
 * ancestor re-scopes `position: fixed` onto itself — a drawer nested inside it
 * would be clipped to the bar.
 *
 * The column width is a single-element value, so it is declared locally rather
 * than raised to `:root` — the design system's override checklist, question 3.
 */
export function AppShell() {
  const navId = useId();
  const navToggleId = useId();
  const navOpen = useNavStore((state) => state.open);
  const setNavOpen = useNavStore((state) => state.setOpen);
  const navigate = useNavigate();
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  useNavTierSync();
  useDocumentTitle();

  /**
   * Sign-out is CONFIRMED before it fires. The console is a working surface —
   * an operator is routinely mid-form when they reach for the account menu, and
   * a menu row one pixel from «الإعدادات» that ends the session on a single
   * click is a trap rather than a control.
   *
   * `warning`, never `danger`: nothing is destroyed. `--danger` is reserved for
   * the destruction it names, and the console puts a genuine delete a row away
   * from this.
   */
  async function onSignOut() {
    // The dialog is dismissed FIRST. `<dialog method="dialog">` closes itself
    // on submit, so a re-render that still says `open` would call `showModal()`
    // on the element the browser has just closed and put the confirmation back
    // on screen behind the sign-out.
    setConfirmingSignOut(false);
    await signOut();
    navigate('/login');
  }

  return (
    <div className="flex min-h-dvh flex-col" style={{ '--sidenav-inline-size': '232px' } as CSSProperties}>
      <a className="skip-link" href="#main">
        {copy.skipToContent}
      </a>

      <Topbar navId={navId} navToggleId={navToggleId} signOut={() => setConfirmingSignOut(true)} />

      {/*
        The scrim leaves the page from `--bp-lg` with the drawer it dims. It is
        a sibling of both, for the containing-block reason above.
      */}
      <div
        aria-hidden="true"
        onClick={() => setNavOpen(false)}
        className={
          navOpen
            ? 'visible fixed inset-0 z-[69] opacity-100 transition-opacity duration-fast ease-standard [background:color-mix(in_srgb,var(--brand-noir)_62%,transparent)] lg:hidden'
            : 'invisible fixed inset-0 z-[69] opacity-0 lg:hidden'
        }
      />

      {/*
        `flex-1` on the content block rather than an auto margin on the footer:
        the auto margin would swallow the footer's section gap on every page
        that actually scrolls.
      */}
      <div className="mx-auto w-full max-w-container flex-1 px-gutter lg:grid lg:items-start lg:gap-8 lg:[grid-template-columns:var(--sidenav-inline-size)_minmax(0,1fr)] xl:gap-9">
        <SideNav id={navId} toggleId={navToggleId} />

        <main id="main" className="min-w-0 self-stretch pb-8">
          <Outlet />
        </main>
      </div>

      <Footer />

      <ConfirmDialog
        open={confirmingSignOut}
        subject={{ ...copy.signOutConfirm, intent: 'warning' }}
        onConfirm={() => void onSignOut()}
        onCancel={() => setConfirmingSignOut(false)}
      />
    </div>
  );
}
